/**
 * Lexical JSON builders in exactly the shapes the editor exports (probed
 * 02.10.2026 against the real node classes). The parser builds these; the
 * headless editor (docEditor.normalize) then fills numbering and anything
 * Lexical derives itself.
 */
import { DOC_MEDIA_ENC_ALG } from '../sanitizeHtml';

const el = (type, children, extra = {}) => ({
  children: children || [],
  direction: null,
  format: extra.format || '',
  indent: extra.indent || 0,
  type,
  version: 1,
  ...extra.fields,
});

export const text = (t, format = 0, style = '') => ({
  detail: 0, format, mode: 'normal', style, text: t, type: 'text', version: 1,
});
export const linebreak = () => ({ type: 'linebreak', version: 1 });
export const tab = () => ({ detail: 2, format: 0, mode: 'normal', style: '', text: '\t', type: 'tab', version: 1 });

export const paragraph = (children, attrs = {}) =>
  el('paragraph', children, { ...attrs, fields: { textFormat: 0, textStyle: '' } });
export const heading = (tag, children, attrs = {}) => el('heading', children, { ...attrs, fields: { tag } });
export const quote = (children, attrs = {}) => el('quote', children, attrs);
export const callout = (kind, children, attrs = {}) => el('callout', children, { ...attrs, fields: { kind } });

export const link = (url, children, { title = null, target = null, rel = null } = {}) =>
  el('link', children, { fields: { rel, target, title, url } });

export const list = (listType, items, start = 1) =>
  el('list', items, { fields: { listType, start, tag: listType === 'number' ? 'ol' : 'ul' } });
export const listItem = (children, checked) =>
  el('listitem', children, { fields: { ...(checked === undefined ? {} : { checked }), value: 1 } });

export function code(language, body) {
  const kids = [];
  String(body).split('\n').forEach((line, i) => {
    if (i > 0) kids.push(linebreak());
    line.split('\t').forEach((part, j) => {
      if (j > 0) kids.push(tab());
      if (part) kids.push(text(part));
    });
  });
  return el('code', kids, { fields: language ? { language } : {} });
}

export const table = (rows, extra = {}) => el('table', rows, { fields: extra });
export const tableRow = (cells) => el('tablerow', cells);
export const tableCell = (children, { headerState = 0, colSpan = 1, rowSpan = 1, backgroundColor = null, width } = {}) =>
  el('tablecell', children, { fields: { backgroundColor, colSpan, headerState, rowSpan, ...(width ? { width } : {}) } });

export const pageLink = ({ docId, scopeType = 'personal', teamId = null, title = '' }) => ({
  type: 'page-link', version: 1, docId, scopeType, teamId, title,
});

export const docVideo = ({ provider, videoId, start = 0 }) => ({ type: 'doc-video', version: 1, provider, videoId, start });

export const docImage = (f) => ({
  type: 'doc-image',
  version: 1,
  src: f.src || '',
  pendingRef: f.pendingRef || '',
  k: f.k || '',
  enc: f.enc || DOC_MEDIA_ENC_ALG,
  name: f.name || '',
  mime: f.mime || '',
  size: f.size || 0,
  w: f.w ?? null,
  h: f.h ?? null,
  alt: f.alt || '',
  width: f.width ?? null,
});

/** A code block's text: its runs joined, line breaks and tabs as characters. */
export function codeText(node) {
  return (node.children || [])
    .map((c) => (c.type === 'linebreak' ? '\n' : c.type === 'tab' ? '\t' : c.text || ''))
    .join('');
}
